import * as React from "react";

/**
 * Table primitives.
 *
 * ScadsysIdentity exports only a bare `<Table>` and then repeats the same header/cell class strings
 * by hand at every call site, so they drift. Here the cell classes live in `Th`/`Td` and nothing
 * downstream needs to know them.
 */

export const Table: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
    // Wide tables scroll inside their own container rather than pushing the page sideways.
    <div className="w-full overflow-x-auto">
        <table className={`min-w-full divide-y divide-border ${className}`}>{children}</table>
    </div>
);

export const Thead: React.FC<{ children: React.ReactNode }> = ({ children }) => <thead className="bg-surface-muted">{children}</thead>;

export const Tbody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <tbody className="divide-y divide-border bg-surface">{children}</tbody>
);

export interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> {
    children: React.ReactNode;
}

export const Tr: React.FC<TrProps> = ({ children, className = "", ...props }) => (
    <tr className={`transition-colors hover:bg-surface-hover ${className}`} {...props}>
        {children}
    </tr>
);

export interface CellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
    children?: React.ReactNode;
    align?: "left" | "right" | "center";
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" };

export const Th: React.FC<CellProps> = ({ children, className = "", align = "left", ...props }) => (
    <th
        scope="col"
        className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-text-weak ${ALIGN[align]} ${className}`}
        {...props}
    >
        {children}
    </th>
);

export const Td: React.FC<React.TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" | "center" }> = ({
    children,
    className = "",
    align = "left",
    ...props
}) => (
    <td className={`px-6 py-4 text-sm text-text ${ALIGN[align]} ${className}`} {...props}>
        {children}
    </td>
);

/** Right-aligned action cell — the icon-button column every list ends with. */
export const TdActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Td align="right" className="whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">{children}</div>
    </Td>
);
